const tableStyles = `
      :host {
        display: block;
        overflow-x: auto;
      }

      table {
        table-layout: fixed;
        border-collapse: collapse;
        width: 100%;
      }

      tr:not(:last-child),
      thead {
        border-bottom: 1px solid oklch(var(--color-on-surface));
      }

      th, td {
        padding: 83px 0;
        text-align: center;
        white-space: nowrap;
        color: oklch(var(--color-on-surface));
        font: var(--text-grotesk-p--small);

        &:first-child {
          width: 40%;
        }
      }

      tbody th {
        text-align: left;
        font: var(--text-grotesk-p--medium);
        font-weight: 500;
        text-transform: uppercase;
      }

      thead th {
        padding-top: 0;
        font: var(--text-grotesk-p--medium);
        font-weight: 500;
        text-transform: uppercase;
      }

      .loading__spinner {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 48px 0;
      }

      .loading__spinner-inner {
        width: 48px;
        height: 48px;
        border: 5px solid oklch(var(--color-surface-container-high));
        border-bottom-color: transparent;
        border-radius: 50%;
        display: inline-block;
        box-sizing: border-box;
        animation: rotation 1s linear infinite;
        margin: 0 auto;
      }

      .error {
        text-align: center;
        font: var(--text-grotesk-p--medium);
        font-weight: 500;

        padding: 24px 0;
        border: 3px solid oklch(0.537 0.1817 20.81);
        color: oklch(0.537 0.1817 20.81);
        background-color: oklch(0.98 0.1817 20.81);
      }

      @keyframes rotation {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @media (max-width: 768px) {
        th, td {
          padding: 24px 0;
          font: var(--text-grotesk-p--medium);
        }

        tbody th {
          font: var(--text-grotesk-p--medium);
        }

        thead th {
          font: var(--text-grotesk-p--medium);
        }
      }
`;

class SizeTable extends HTMLElement {
  constructor() {
    super();

    this.attachShadow({ mode: 'open' });
  }

  async connectedCallback() {
    // Accept either "src" or "url" as the attribute for the CSV location
    const src = this.getAttribute('src') || this.getAttribute('url');

    if (!src) {
      this.#renderError('Missing "src" attribute on <size-table>.');
      return;
    }

    this.#renderLoading();

    try {
      const response = await fetch(src);
      if (!response.ok) {
        throw new Error(
          `Network response was not ok (status ${response.status})`
        );
      }

      const csvText = await response.text();
      const data = this.#parseCSV(csvText);
      this.#renderTable(data);
    } catch (error) {
      this.#renderError(error.message);
    }
  }

  /* ------------------------------ RENDERING ------------------------------ */

  #renderLoading() {
    const style = document.createElement('style');
    style.textContent = tableStyles;

    this.shadowRoot.innerHTML =
      '<div class="loading__spinner"><span class="loading__spinner-inner"></span></div>';
    this.shadowRoot.append(style);
  }

  #renderError(message) {
    const style = document.createElement('style');
    style.textContent = tableStyles;

    this.shadowRoot.innerHTML = `<p role="alert" class="error">Unable to load size table: ${message}</p>`;
    this.shadowRoot.append(style);
  }

  #renderTable(data) {
    if (!data.length) {
      this.#renderError('CSV was empty.');
      return;
    }

    const [headerRow, ...bodyRows] = data;

    const table = document.createElement('table');
    table.setAttribute('role', 'table');

    // Build thead
    const thead = document.createElement('thead');
    const headerTr = document.createElement('tr');

    headerRow.forEach((cell, index) => {
      const th = document.createElement('th');
      th.textContent = cell;
      th.scope = 'col';
      // Make the first column header sticky (optional — comment out if unwanted)
      if (index === 0) th.scope = 'row';
      headerTr.appendChild(th);
    });

    thead.appendChild(headerTr);

    // Build tbody
    const tbody = document.createElement('tbody');

    bodyRows.forEach((row) => {
      if (row.every((c) => c === '')) return; // Skip empty rows
      const tr = document.createElement('tr');

      row.forEach((cell, idx) => {
        const cellEl = document.createElement(idx === 0 ? 'th' : 'td');
        if (idx === 0) cellEl.scope = 'row';
        cellEl.textContent = cell;
        tr.appendChild(cellEl);
      });

      tbody.appendChild(tr);
    });

    table.appendChild(thead);
    table.appendChild(tbody);

    const style = document.createElement('style');
    style.textContent = tableStyles;

    // Inject into shadow root
    this.shadowRoot.innerHTML = '';
    this.shadowRoot.append(style, table);
  }

  /* ------------------------------ CSV PARSER ----------------------------- */

  /**
   * Lightweight CSV parser supporting quoted fields and escaped quotes.
   * @param {string} text Raw CSV text
   * @returns {string[][]} 2-D array representing the rows and cells
   */
  #parseCSV(text) {
    const rows = [];
    let currentCell = '';
    let currentRow = [];
    let inQuotes = false;

    const pushCell = () => {
      currentRow.push(currentCell.trim());
      currentCell = '';
    };

    const pushRow = () => {
      if (currentRow.length) rows.push(currentRow);
      currentRow = [];
    };

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (inQuotes) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            // Escaped quote
            currentCell += '"';
            i++; // Skip next quote
          } else {
            inQuotes = false; // Closing quote
          }
        } else {
          currentCell += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true; // Opening quote
        } else if (char === ',') {
          pushCell();
        } else if (char === '\n') {
          pushCell();
          pushRow();
        } else if (char === '\r') {
          // Ignore CR (\r) in CRLF sequences
          continue;
        } else {
          currentCell += char;
        }
      }
    }

    // Flush the last cell/row
    pushCell();
    pushRow();

    return rows;
  }
}

customElements.define('size-table', SizeTable);
